import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    useTheme,
    alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, Variants } from 'framer-motion';
import React from 'react';
import { getOrDefault } from '../../../../Helpers/Utils';
import { MissionVisionProps } from './types';

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 24,
    overflow: 'hidden',
    height: '100%',
    position: 'relative',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    cursor: 'pointer',
    background: 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.3)',
    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.08)',
    
    '&:hover': {
        transform: 'translateY(-12px) scale(1.02)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
        background: 'rgba(255, 255, 255, 0.85)',
        
        '& .icon-container': {
            transform: 'scale(1.1) rotate(5deg)',
        },
        
        '& .gradient-overlay': {
            opacity: 1,
        }
    }
}));

const IconContainer = styled(Box)<{ iconColor?: string }>(({ theme, iconColor }) => ({
    width: 80,
    height: 80,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    background: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(10px)',
    border: `2px solid ${alpha(iconColor || theme.palette.primary.main, 0.3)}`,
    boxShadow: `0 8px 24px ${alpha(iconColor || theme.palette.primary.main, 0.25)}`,
    transition: 'all 0.3s ease',
    fontSize: '2.5rem',
    color: iconColor || theme.palette.primary.main,
    position: 'relative',
    
    '&::after': {
        content: '""',
        position: 'absolute',
        inset: -8,
        borderRadius: '50%',
        background: `conic-gradient(from 0deg, transparent, ${iconColor || theme.palette.primary.main}, transparent)`,
        opacity: 0,
        transition: 'opacity 0.4s ease',
        animation: 'rotate 3s linear infinite',
        zIndex: -1
    },
    
    '@keyframes rotate': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' }
    }
}));

const GradientOverlay = styled(Box)<{ overlayColor?: string }>(({ overlayColor }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${alpha(overlayColor || '#5083fb', 0.05)} 0%, transparent 100%)`,
    opacity: 0,
    transition: 'opacity 0.5s ease',
    zIndex: 0,
    pointerEvents: 'none'
}));

const MissionVision: React.FC<MissionVisionProps> = ({ spec, type, index }) => {
    const theme = useTheme();

    // Component configuration
    const componentSpec = getOrDefault(spec, {});
    const backgroundColor = getOrDefault(componentSpec.backgroundColor, theme.palette.background.default);
    const sectionTitle = getOrDefault(componentSpec.sectionTitle, 'Mission & Vision');
    const titleColor = getOrDefault(componentSpec.titleColor, theme.palette.text.primary);
    const cardTextColor = getOrDefault(componentSpec.cardTextColor, theme.palette.text.primary);
    const missionIconColor = getOrDefault(componentSpec.missionIconColor, theme.palette.primary.main);
    const visionIconColor = getOrDefault(componentSpec.visionIconColor, theme.palette.secondary.main);
    
    // Mission content
    const missionTitle = getOrDefault(componentSpec.missionTitle, 'Our Mission');
    const missionContent = getOrDefault(componentSpec.missionContent, 'To provide exceptional service and create memorable experiences for our customers while maintaining the highest standards of quality and innovation.');
    const missionIcon = getOrDefault(componentSpec.missionIcon, '🎯');
    
    // Vision content
    const visionTitle = getOrDefault(componentSpec.visionTitle, 'Our Vision');
    const visionContent = getOrDefault(componentSpec.visionContent, 'To be the leading company in our industry, recognized for our commitment to excellence, innovation, and customer satisfaction.');
    const visionIcon = getOrDefault(componentSpec.visionIcon, '🚀');

    // Animation variants
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants: Variants = {
        hidden: { 
            opacity: 0, 
            y: 60,
            scale: 0.8
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring" as const,
                stiffness: 100,
                damping: 12,
                duration: 0.6
            }
        }
    };

    const titleVariants: Variants = {
        hidden: { 
            opacity: 0, 
            y: 30
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: "easeOut" as const
            }
        }
    };

    return (
        <Box
            id={spec.id}
            sx={{
                background: backgroundColor,
                minHeight: '70vh',
                py: { xs: 6, md: 8 },
                px: { xs: 2, md: 4 },
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Animated background elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '15%',
                    left: '-8%',
                    width: 500,
                    height: 500,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(missionIconColor, 0.12)}, transparent)`,
                    filter: 'blur(80px)',
                    animation: 'float-slow 20s ease-in-out infinite',
                    '@keyframes float-slow': {
                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                        '50%': { transform: 'translate(80px, 80px) scale(1.1)' }
                    }
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '15%',
                    right: '-8%',
                    width: 450,
                    height: 450,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(visionIconColor, 0.12)}, transparent)`,
                    filter: 'blur(70px)',
                    animation: 'float-reverse-slow 18s ease-in-out infinite',
                    '@keyframes float-reverse-slow': {
                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                        '50%': { transform: 'translate(-70px, -70px) scale(1.12)' }
                    }
                }}
            />

            {/* Section Title */}
            {sectionTitle && (
                <motion.div
                    variants={titleVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
                        <Typography
                            variant="h2"
                            sx={{
                                color: titleColor,
                                fontWeight: 800,
                                fontSize: { xs: '2.5rem', md: '3rem', lg: '3.5rem' },
                                lineHeight: 1.2,
                                mb: 2,
                                background: `linear-gradient(45deg, ${titleColor}, ${missionIconColor})`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            {sectionTitle}
                        </Typography>
                    </Box>
                </motion.div>
            )}

            {/* Mission & Vision Cards */}
            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
            >
                <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative', zIndex: 1 }}>
                    <Grid container spacing={{ xs: 3, md: 4 }}>
                        {/* Mission Card */}
                        <Grid item xs={12} md={6}>
                            <motion.div variants={itemVariants}>
                                <StyledCard>
                                    <GradientOverlay className="gradient-overlay" overlayColor={missionIconColor} />
                                    <CardContent 
                                        sx={{ 
                                            p: { xs: 3, md: 4 },
                                            textAlign: 'center',
                                            position: 'relative',
                                            zIndex: 2,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <IconContainer 
                                            className="icon-container"
                                            iconColor={missionIconColor}
                                        >
                                            {missionIcon}
                                        </IconContainer>
                                        
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                color: cardTextColor,
                                                fontWeight: 700,
                                                mb: 3,
                                                fontSize: { xs: '1.5rem', md: '1.8rem' }
                                            }}
                                        >
                                            {missionTitle}
                                        </Typography>
                                        
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: cardTextColor,
                                                lineHeight: 1.7,
                                                fontSize: { xs: '1rem', md: '1.1rem' },
                                                opacity: 0.9
                                            }}
                                        >
                                            {missionContent}
                                        </Typography>
                                    </CardContent>
                                </StyledCard>
                            </motion.div>
                        </Grid>

                        {/* Vision Card */}
                        <Grid item xs={12} md={6}>
                            <motion.div variants={itemVariants}>
                                <StyledCard>
                                    <GradientOverlay className="gradient-overlay" overlayColor={visionIconColor} />
                                    <CardContent 
                                        sx={{ 
                                            p: { xs: 3, md: 4 },
                                            textAlign: 'center',
                                            position: 'relative',
                                            zIndex: 2,
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <IconContainer 
                                            className="icon-container"
                                            iconColor={visionIconColor}
                                        >
                                            {visionIcon}
                                        </IconContainer>
                                        
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                color: cardTextColor,
                                                fontWeight: 700,
                                                mb: 3,
                                                fontSize: { xs: '1.5rem', md: '1.8rem' }
                                            }}
                                        >
                                            {visionTitle}
                                        </Typography>
                                        
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: cardTextColor,
                                                lineHeight: 1.7,
                                                fontSize: { xs: '1rem', md: '1.1rem' },
                                                opacity: 0.9
                                            }}
                                        >
                                            {visionContent}
                                        </Typography>
                                    </CardContent>
                                </StyledCard>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Box>
            </motion.div>
        </Box>
    );
};

export default MissionVision;
